import React, { useEffect, useState, useRef } from "react";
import { Typography } from "@mhome/ui";
import { kelvinToRGB } from "../common/color-util";
import SyncProblemIcon from "@mui/icons-material/SyncProblem";
import { cn } from "../../lib/utils";

const ColorTempNumberDetail1Widget = ({
  widget,
  onValueUpdate,
  pattern,
  isEditing = false,
}) => {
  const [colorTemp, setColorTemp] = useState(2700);
  const [widgetData, setWidgetData] = useState(null);
  const [tempMinMax, setTempMinMax] = useState({ min: 2700, max: 6500 });
  const dragging = useRef(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const sliderRef = React.useRef(null);
  const latestColorTempRef = useRef(2700);
  const touchStartRef = useRef(null);
  const isScrolling = useRef(false);

  const disabled = pattern && pattern.endsWith("_R");
  const showWarning = pattern && pattern.endsWith("_W");

  useEffect(() => {
    if (!widget) return;
    setWidgetData(widget);

    if (widget.value) {
      setColorTemp(widget.value || 2700);
    }
  }, [widget]);

  const handleValueUpdate = (data) => {
    if (isEditing || !widgetData) return;

    const updatedWidget = {
      ...widgetData,
      value: data,
    };

    onValueUpdate(updatedWidget);
  };

  const gradientStops = [];
  const steps = 10;

  for (let i = 0; i <= steps; i++) {
    const temp =
      tempMinMax.min + (i / steps) * (tempMinMax.max - tempMinMax.min);
    const rgb = kelvinToRGB(temp);
    gradientStops.push(`rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`);
  }

  const gradientStyle = {
    background: `linear-gradient(to right, ${gradientStops.join(", ")})`,
  };

  const calculateTemperature = (clientX) => {
    const rect = sliderRef.current.getBoundingClientRect();
    const percentage = (clientX - rect.left) / rect.width;
    const clampedPercentage = Math.max(0, Math.min(1, percentage));
    return Math.round(
      tempMinMax.min + clampedPercentage * (tempMinMax.max - tempMinMax.min)
    );
  };

  const handleInteraction = (clientX) => {
    if (isEditing || disabled) return;
    const newTemp = calculateTemperature(clientX);
    if (newTemp !== colorTemp) {
      setColorTemp(newTemp);
      latestColorTempRef.current = newTemp;
    }
  };

  const handleMouseMove = (e) => {
    if (!dragging.current) return;
    e.preventDefault();
    requestAnimationFrame(() => {
      handleInteraction(e.clientX);
    });
  };

  const handleMouseUp = () => {
    if (!dragging.current) return;
    dragging.current = false;
    setShowTooltip(false);
    handleValueUpdate(latestColorTempRef.current);
    window.removeEventListener("mousemove", handleMouseMove);
    window.removeEventListener("mouseup", handleMouseUp);
  };

  const handleMouseDown = (e) => {
    if (isEditing || disabled) return;
    e.preventDefault();
    dragging.current = true;
    setShowTooltip(true);
    handleInteraction(e.clientX);
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
  };

  const handleTouchMove = (e) => {
    if (isScrolling.current || !touchStartRef.current) return;

    if (dragging.current) {
      e.preventDefault();
      requestAnimationFrame(() => {
        handleInteraction(e.touches[0].clientX);
      });
      return;
    }

    const deltaX = Math.abs(e.touches[0].clientX - touchStartRef.current.x);
    const deltaY = Math.abs(e.touches[0].clientY - touchStartRef.current.y);
    const moveThreshold = 5;

    if (deltaX > moveThreshold || deltaY > moveThreshold) {
      if (deltaY > deltaX) {
        isScrolling.current = true;
        touchStartRef.current = null;
        window.removeEventListener("touchmove", handleTouchMove);
        window.removeEventListener("touchend", handleTouchEnd);
      } else {
        e.preventDefault();
        dragging.current = true;
        setShowTooltip(true);
        requestAnimationFrame(() => {
          handleInteraction(e.touches[0].clientX);
        });
      }
    }
  };

  const handleTouchEnd = (e) => {
    if (isScrolling.current) {
      isScrolling.current = false;
      touchStartRef.current = null;
      return;
    }

    if (dragging.current) {
      setShowTooltip(false);
      handleValueUpdate(latestColorTempRef.current);
    } else {
      handleInteraction(e.changedTouches[0].clientX);
      handleValueUpdate(latestColorTempRef.current);
    }

    dragging.current = false;
    isScrolling.current = false;
    touchStartRef.current = null;
    window.removeEventListener("touchmove", handleTouchMove);
    window.removeEventListener("touchend", handleTouchEnd);
  };

  const handleTouchStart = (e) => {
    if (isEditing || disabled) return;
    dragging.current = false;
    isScrolling.current = false;
    touchStartRef.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
    };
    window.addEventListener("touchmove", handleTouchMove, { passive: false });
    window.addEventListener("touchend", handleTouchEnd);
  };

  const thumbPosition =
    ((colorTemp - tempMinMax.min) / (tempMinMax.max - tempMinMax.min)) * 100;

  return (
    <div className="w-full flex flex-col gap-4">
      {/* Color Temperature Controls Section */}
      <div
        className="flex flex-col rounded-2xl p-3"
        style={{
          backgroundColor: "hsl(var(--card))",
        }}
      >
        <Typography
          variant="body2"
          className="mb-3 font-bold flex items-center gap-2"
        >
          Color Temperature
          {showWarning && (
            <SyncProblemIcon
              className="text-inherit"
              style={{ color: "hsl(var(--warning))" }}
            />
          )}
        </Typography>

        <div
          className="relative w-full h-8 flex items-center"
          onClick={(e) => e.stopPropagation()}
        >
          {showTooltip && (
            <div
              className="absolute -top-[25px] -translate-x-1/2 bg-white rounded-lg px-2 py-1 text-black text-sm font-bold z-[1] pointer-events-none shadow-lg"
              style={{
                left: `${thumbPosition}%`,
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3)",
              }}
            >
              {colorTemp}K
            </div>
          )}
          <div
            ref={sliderRef}
            className={cn(
              "relative w-full h-full rounded-2xl select-none outline-none",
              isEditing || disabled ? "cursor-default" : "cursor-pointer"
            )}
            style={{
              opacity: disabled ? 0.8 : 1,
              WebkitTapHighlightColor: "transparent",
              WebkitTouchCallout: "none",
              WebkitUserSelect: "none",
              ...gradientStyle,
            }}
            onMouseDown={handleMouseDown}
            onTouchStart={handleTouchStart}
          >
            <div
              className="absolute w-1 bg-white rounded shadow-sm top-1/2 -translate-y-1/2 pointer-events-none"
              style={{
                height: "60%",
                left: `${thumbPosition}%`,
                boxShadow: "0 0 4px rgba(0, 0, 0, 0.3)",
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ColorTempNumberDetail1Widget;
